<template>
  <Stack
    class="stk-pt-1"
    gap="3"
  >
    <div>
      <Subtitle>
        <label class="stk-radio">
          <input
            v-model="frequency"
            type="radio"
            value="daily"
          >
          Every Day
        </label>
      </Subtitle>
    </div>

    <div>
      <Subtitle>
        <label class="stk-radio">
          <input
            v-model="frequency"
            type="radio"
            value="weekly"
          >
          Weekly
        </label>
      </Subtitle>

      <Stack
        gap="1"
        class="frequency-content"
        :class="{ 'is-faded': frequency !== 'weekly'}"
      >
        <div class="weekdays-list stk-checkboxes stk-regap-2">
          <label
            v-for="weekday in WEEKDAYS"
            :key="weekday.label"
            class="stk-checkbox"
          >
            <input
              v-model="selectedWeekdays"
              type="checkbox"
              :value="weekday.value"
            >
            {{ weekday.label }}
          </label>
        </div>

        <Buttons class="weekdays-select">
          Select
          <button
            class="stk-reset"
            type="button"
            @click="handleClick('all')"
          >
            All
          </button>
          <button
            class="stk-reset"
            type="button"
            @click="handleClick('weekday')"
          >
            Weekdays
          </button>
          <button
            class="stk-reset"
            type="button"
            @click="handleClick('none')"
          >
            None
          </button>
        </Buttons>
      </Stack>
    </div>

    <div>
      <Subtitle>
        <label class="stk-radio">
          <input
            v-model="frequency"
            type="radio"
            value="monthly"
          >
          Monthly
        </label>
      </Subtitle>

      <div
        class="frequency-content"
        :class="{ 'is-faded': frequency !== 'monthly'}"
      >
        <span>
          Every
        </span>

        <select
          v-model="selectedDayOfMonth"
          class="stk-soft-select"
        >
          <option
            v-for="opt in DAY_OF_MONTH_OPTIONS"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
          <option
            key="L"
            value="L"
          >
            last day
          </option>
        </select>

        <span>
          of the month
        </span>
      </div>
    </div>

    <Stack gap="1">
      <Label>Time of day</Label>

      <div>
        <select
          v-model="selectedHour"
          class="stk-resize-3_5 stk-soft-select"
        >
          <option
            v-for="opt in HOUR_OPTIONS"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>
    </Stack>
  </Stack>
</template>

<script setup>
import { computed, defineEmits, ref, watch } from 'vue';
import { Buttons, Label, Stack, Subtitle } from '@stakeui';
// eslint-disable-next-line camelcase
import { ordinal_suffix_of } from '@frontend/shared/Util.js';
import { convertLocalCronToUtc, convertUtcCronToLocal, getBrowserTimeZone } from '../cronUtils';

const emit = defineEmits(['change']);

const props = defineProps({
  cron: {
    type: String,
    required: true,
  },
});

const MINUTE = '0';
const DEFAULT_HOUR = '6'; // 6AM
const MONTH = '*'; // Every month

const isInitialized = ref('daily');
const frequency = ref('daily');
const selectedWeekdays = ref(['1']);
const selectedDayOfMonth = ref('1');
const selectedHour = ref(DEFAULT_HOUR);

const timeZoneLabel = getBrowserTimeZone();

// Constants
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => {
  const hour12 = ((h + 11) % 12) + 1; // 0 → 12, 13 → 1
  const suffix = h < 12 ? 'AM' : 'PM';
  return {
    value: String(h), // cron needs 24-hour
    label: `${hour12} ${suffix}`, // UI shows 12-hour
  };
});

const WEEKDAYS = [
  {
    label: 'Sunday',
    value: '0',
  },
  {
    label: 'Monday',
    value: '1',
  },
  {
    label: 'Tuesday',
    value: '2',
  },
  {
    label: 'Wednesday',
    value: '3',
  },
  {
    label: 'Thursday',
    value: '4',
  },
  {
    label: 'Friday',
    value: '5',
  },
  {
    label: 'Saturday',
    value: '6',
  },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => {
  const day = i + 1;

  return {
    value: String(day),
    label: ordinal_suffix_of(day),
  };
});

// Utilities
const buildWeekdayField = weekdays => {
  if (!weekdays || weekdays.length === 0) {
    return '*'; // default: every day
  }

  const cleaned = [...new Set(weekdays.map(String))]
      .sort((a, b) => Number(a) - Number(b));

  return cleaned.join(',');
};

const parseCron = cron => {
  if (!cron) return;

  // Convert backend UTC cron into local equivalent for display
  const localCron = convertUtcCronToLocal(cron);
  const parts = (localCron || cron).trim().split(/\s+/);
  if (parts.length !== 5) return;

  // eslint-disable-next-line no-unused-vars
  const [minute, hour, day, month, weekday] = parts;

  // set hour from cron (fallback to default if '*')
  if (hour && hour !== '*') {
    selectedHour.value = hour;
  } else {
    selectedHour.value = DEFAULT_HOUR;
  }

  // Monthly mode
  // If day-of-month is specific AND weekday field is *
  if (day !== '*' && weekday === '*') {
    frequency.value = 'monthly';
    selectedDayOfMonth.value = day; // "1" → 1st, "15" → 15th, etc.
    return;
  }

  // Weekly mode
  // If weekday field is not "*"
  if (weekday !== '*') {
    frequency.value = 'weekly';
    selectedWeekdays.value = weekday.split(','); // e.g. "1,3,5"
    return;
  }

  // Daily mode
  frequency.value = 'daily';
};

// Events
const handleClick = option => {
  if (option === 'all') {
    return selectedWeekdays.value = ['0', '1', '2', '3', '4', '5', '6'];
  }

  if (option === 'weekday') {
    return selectedWeekdays.value = ['0', '1', '2', '3', '4'];
  }

  return selectedWeekdays.value = [];
};

// Computed
const finalCronString = computed(() => {
  const hour = selectedHour.value || DEFAULT_HOUR;

  if (frequency.value === 'weekly') {
    const weekdayField = buildWeekdayField(selectedWeekdays.value);
    const localParts = [MINUTE, hour, '*', MONTH, weekdayField].join(' ');
    return convertLocalCronToUtc(localParts);
  }

  if (frequency.value === 'monthly') {
    const localParts = [MINUTE, hour, selectedDayOfMonth.value, MONTH, '*'].join(' ');
    return convertLocalCronToUtc(localParts);
  }

  const localParts = [MINUTE, hour, '*', MONTH, '*'].join(' '); // Every day
  return convertLocalCronToUtc(localParts);
});

// Lifecycle
watch(
    () => props.cron,
    newVal => {
      parseCron(newVal);
      isInitialized.value = true;
    },
    { immediate: true },
);

watch(finalCronString, newVal => {
  if (!isInitialized.value) return;
  if (newVal === props.cron) return;
  emit('change', { cron: newVal });
});
</script>

<style scoped>
.frequency-content {
  font-size: 0.875em;
  padding: 0.5em 1em 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity;

  &.is-faded {
    opacity: 0.3;
    pointer-events: none;
  }
}

.weekdays-select {
  color: var(--stk-text-weak);
  gap: 0.5em;

  button {
    cursor: pointer;
    text-decoration: underline;
  }
}
</style>
